import { Flex, Icon, SimpleGrid, Box } from "@chakra-ui/react";
import { FormikProps } from "formik";
import Select from "react-select";
import { useEffect, useState, useMemo } from "react";
import { BaseInput } from "../../Controls/BaseInput";
import { BaseControl } from "../../Controls/BaseControl";
import { BaseTextArea } from "../../Controls/BaseTextArea";
import { LeadForm } from "@/pages/CreateLeadPage";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";
import { FiUser } from "react-icons/fi";
import api from "@/api";
import { LeadContact } from "@/model/lead";
import { useAppSelector } from "@/store";
import { RootState } from "@/store";
import { transformGuides } from "@/helpers/transformGuides";

const colourStyles = {
  control: (styles: any) => ({ ...styles, borderColor: "grey.200" }),
};

interface CreateLeadBlockProps {
  formik: FormikProps<LeadForm>;
  contacts?: LeadContact[];
}

const yesNoOptions = [
  { value: "yes", label: "Да" },
  { value: "no", label: "Нет" },
];

export const CreateLeadBlock = ({ formik, contacts = [] }: CreateLeadBlockProps) => {
  const { guides } = useAppSelector((state: RootState) => state.guides);
  const [recruitersOptions, setRecruitersOptions] = useState<any[]>([]);
  const [contactsOptions, setContactsOptions] = useState<{ value: number; label: string }[]>([]);

  const leadSourcesOptions = useMemo(() => {
    if (!guides) return [];
    return transformGuides((guides as any)?.LeadSource || {});
  }, [guides]);

  const statusOptions = useMemo(() => {
    if (!guides) return [];
    return transformGuides((guides as any)?.LeadStatus || {});
  }, [guides]);

  useEffect(() => {
    api.recruiters.recruiters({ customer: true }).then((res) => {
      const resultRecruiters = res.data.map((el: any) => ({
        value: el.id,
        label: el.username,
      }));
      setRecruitersOptions(resultRecruiters);
    });
  }, []);

  useEffect(() => {
    const resultContacts = contacts.map((contact) => ({
      value: contact.id,
      label: contact.name,
    }));
    setContactsOptions(resultContacts);
  }, [contacts]);

  return (
    <ViewInformationBlock icon={<Icon as={FiUser as any} boxSize={5} color="#3b515e" />} title="Основная информация">
      <Flex flexDirection="column" gap="24px">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <BaseInput
            fieldTitle="Название"
            requiredField={true}
            value={formik.values.name}
            onChange={formik.handleChange}
            placeholder="Название лида"
            name="name"
          />
          <BaseControl fieldTitle="Ответственный" requiredField>
            <Select
              menuPosition="fixed"
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="responsible_user_id"
              options={recruitersOptions}
              styles={colourStyles}
              placeholder="Выберите ответственного"
              value={recruitersOptions.find((o) => o.value === formik.values.responsible_user_id) || null}
              onChange={(arg) => {
                formik.setFieldValue("responsible_user_id", arg?.value || null);
              }}
            />
          </BaseControl>
          <BaseControl fieldTitle="Контакты">
            <Select
              menuPosition="fixed"
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="contact_id"
              options={contactsOptions}
              styles={colourStyles}
              placeholder="Выберите контакт"
              value={contactsOptions.find((o) => o.value === formik.values.contact_id) || null}
              onChange={(arg) => {
                formik.setFieldValue("contact_id", arg?.value || null);
              }}
            />
          </BaseControl>
          <BaseControl fieldTitle="Источник лида">
            <Select
              menuPosition="fixed"
              className="basic-single"
              classNamePrefix="select"
              isSearchable={true}
              name="source"
              options={leadSourcesOptions}
              styles={colourStyles}
              placeholder="Выберите источник"
              value={leadSourcesOptions.find((o) => o.value === formik.values.source) || null}
              onChange={(arg) => {
                formik.setFieldValue("source", arg?.value || null);
              }}
            />
          </BaseControl>
        </SimpleGrid>

        <BaseInput fieldTitle="Сайт" value={formik.values.website} onChange={formik.handleChange} placeholder="https://example.com" name="website" />
        <BaseTextArea
          fieldTitle="Комментарий"
          value={formik.values.comment}
          onChange={formik.handleChange}
          name="comment"
          placeholder="Дополнительная информация..."
        />

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="16px"
            padding="16px"
            border="1px solid #E2E8F0"
            borderRadius="8px"
            bg="white"
          >
            <Flex fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              NDA
            </Flex>
            <Box flex="1" minW="120px">
              <Select
                menuPosition="fixed"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                name="nda"
                options={yesNoOptions}
                styles={colourStyles}
                value={yesNoOptions.find((o) => o.value === formik.values.nda) || null}
                onChange={(arg) => {
                  formik.setFieldValue("nda", arg?.value || null);
                }}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="16px"
            padding="16px"
            border="1px solid #E2E8F0"
            borderRadius="8px"
            bg="white"
          >
            <Flex fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              Анкета
            </Flex>
            <Box flex="1" minW="120px">
              <Select
                menuPosition="fixed"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                name="questionnaire"
                options={yesNoOptions}
                styles={colourStyles}
                value={yesNoOptions.find((o) => o.value === formik.values.questionnaire) || null}
                onChange={(arg) => {
                  formik.setFieldValue("questionnaire", arg?.value || null);
                }}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="16px"
            padding="16px"
            border="1px solid #E2E8F0"
            borderRadius="8px"
            bg="white"
          >
            <Flex fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              Созвон NDA
            </Flex>
            <Box flex="1" minW="120px">
              <Select
                menuPosition="fixed"
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                name="nda_call"
                options={yesNoOptions}
                styles={colourStyles}
                value={yesNoOptions.find((o) => o.value === formik.values.nda_call) || null}
                onChange={(arg) => {
                  formik.setFieldValue("nda_call", arg?.value || null);
                }}
              />
            </Box>
          </Box>
        </SimpleGrid>

        <BaseControl fieldTitle="Статус" requiredField>
          <Select
            menuPosition="fixed"
            className="basic-single"
            classNamePrefix="select"
            isSearchable={true}
            name="status"
            options={statusOptions}
            styles={colourStyles}
            placeholder="Выберите статус"
            value={statusOptions.find((o) => o.value === formik.values.status) || null}
            onChange={(arg) => {
              formik.setFieldValue("status", arg?.value || null);
            }}
          />
        </BaseControl>
      </Flex>
    </ViewInformationBlock>
  );
};
